import { motion } from "framer-motion";
import { PlayCircle, Download, BookOpen, Landmark } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

// -------------------- Articoli disponibili --------------------
const articles = {
  "anemia-sideropenica": {
    tipo: "Guida",
    icon: BookOpen,
    titolo: "Cos'è l'anemia sideropenica",
    descrizione:
      "Guida completa: cause, sintomi, diagnosi e cura dell'anemia da carenza di ferro.",
    intro:
      "L'anemia sideropenica è una riduzione dei globuli rossi e dell'emoglobina (la \u201Cproteina\u201D del sangue che trasporta ossigeno) causata da una carenza di ferro. Il ferro è un nutriente essenziale: se manca, il midollo osseo produce globuli rossi più piccoli e \u201Cpiù poveri\u201D di emoglobina, e ai tessuti arriva meno ossigeno.",
    sections: [
      {
        heading: "Perché il ferro è importante",
        paragraphs: [
          "Il ferro serve soprattutto per costruire l'emoglobina, che permette ai globuli rossi di portare ossigeno in tutto il corpo. Se le \u201Cscorte\u201D di ferro si svuotano, l'organismo non riesce a produrre globuli rossi efficaci e può comparire l'anemia.",
        ],
      },
      {
        heading: "Sintomi possibili",
        paragraphs: ["I sintomi possono variare da persona a persona. I più comuni sono:"],
        list: [
          "Stanchezza marcata e ridotta energia",
          "Fiato corto (anche per sforzi lievi)",
          "Capogiri, mal di testa",
          "Palpitazioni (cuore che batte più forte o più veloce)",
          "Pallore",
          "Difficoltà di concentrazione",
          "A volte voglia di mangiare cose non alimentari (pica, ad esempio ghiaccio) o fastidi alle gambe tipo \u201Cirrequietezza\u201D",
        ],
        afterList: "Se i sintomi sono importanti o peggiorano, è importante parlarne con il medico.",
      },
      {
        heading: "Cause più frequenti",
        paragraphs: ["L'anemia sideropenica può dipendere da una o più di queste situazioni:"],
        list: [
          "Perdita di sangue (la causa più comune): mestruazioni abbondanti; perdita di sangue dall'apparato digerente (stomaco/intestino), anche lenta e non visibile",
          "Poco ferro nella dieta (per esempio alimentazione sbilanciata)",
          "Assorbimento ridotto del ferro (per esempio celiachia, interventi sullo stomaco/intestino, alcune infiammazioni intestinali)",
          "Aumentato fabbisogno (per esempio gravidanza)",
        ],
        callout:
          "Nota importante: negli adulti, soprattutto uomini e donne dopo la menopausa, una carenza di ferro va spesso approfondita per cercare e curare la causa.",
      },
      {
        heading: "Come si fa la diagnosi",
        paragraphs: ["La diagnosi si fa con esami del sangue, in particolare:"],
        list: [
          "Emocromo (per vedere emoglobina e globuli rossi)",
          "Ferritina (una misura delle scorte di ferro)",
          "A volte anche altri esami del ferro (come saturazione della transferrina), soprattutto se c'è infiammazione o altre malattie.",
        ],
      },
      {
        heading: "Come si cura",
        paragraphs: ["La cura dipende dalla causa e dalla gravità, ma in genere include:"],
        subsections: [
          {
            title: "1) Curare la causa della carenza di ferro",
            text: "Per esempio: trattare perdite di sangue, problemi intestinali o cause ginecologiche.",
          },
          {
            title: "2) Ripristinare il ferro",
            list: [
              "Ferro per bocca (compresse/gocce): è spesso il primo passo. In molti casi si prende una volta al giorno; in alcune persone può essere più tollerabile a giorni alterni.",
              "Ferro per vena (endovena): può essere usato se il ferro per bocca non funziona, non si assorbe bene, o non è tollerato.",
            ],
          },
          {
            title: "3) Controllare la risposta alla terapia",
            text: "Di solito il medico controlla gli esami dopo l'inizio della terapia per verificare che l'emoglobina stia risalendo e che le scorte si stiano ricostituendo.",
          },
        ],
      },
      {
        heading: "Consigli pratici sull'alimentazione",
        paragraphs: ["(aiutano, ma spesso non bastano da soli)"],
        list: [
          "Fonti di ferro \u201Cpiù assorbibile\u201D: carne, pesce, pollame.",
          "Buone fonti vegetali: legumi (lenticchie, fagioli), semi, frutta secca, alcuni cereali \u201Carricchiti\u201D. Il ferro vegetale si assorbe meno facilmente.",
          "Per assorbire meglio il ferro vegetale, abbina vitamina C (es. agrumi, kiwi, peperoni, pomodori) durante i pasti.",
          "Alcune bevande/alimenti possono ridurre l'assorbimento del ferro se presi insieme (es. tè e caffè).",
        ],
      },
      {
        heading: "Quando contattare subito il medico o andare in urgenza",
        list: [
          "Dolore al petto, fiato corto importante, svenimento",
          "Battito molto accelerato a riposo",
          "Feci nere, sangue nelle feci, vomito con sangue",
          "Peggioramento rapido dei sintomi",
        ],
      },
    ],
    disclaimer:
      "Questa guida non sostituisce il parere medico. Se i sintomi peggiorano o hai nuovi dubbi, contatta il tuo medico o rivolgiti al pronto soccorso.",
  },
  "prima-visita-ematologica": {
    tipo: "Guida",
    icon: BookOpen,
    titolo: "Prepararsi alla prima visita ematologica",
    descrizione:
      "Documentazione, farmaci, esami, sintomi da segnalare: come arrivare preparati alla visita.",
    intro:
      "Una visita ematologica ben preparata permette allo specialista di valutare rapidamente il quadro clinico e di impostare eventuali esami o terapie in modo mirato. Ecco cosa serve sapere e portare con te.",
    sections: [
      {
        heading: "Cosa portare (la parte più importante)",
        list: [
          {
            text: "Elenco completo e aggiornato di tutti i farmaci che prendi (nome, dose, orario), inclusi:",
            sublist: [
              "anticoagulanti (es. warfarin, apixaban, rivaroxaban, dabigatran, eparine)",
              "antiaggreganti (es. aspirina, clopidogrel)",
              "cortisonici, immunosoppressori, chemioterapici, integratori \u201Cnaturali\u201D e fitoterapici",
            ],
            after: "Questo aiuta a valutare rischi di sanguinamento, interazioni e necessità di eventuali sospensioni prima di procedure (es. biopsie).",
          },
          {
            text: "Tutti gli esami del sangue recenti (se li hai), in particolare:",
            sublist: [
              "emocromo (globuli bianchi, emoglobina, piastrine)",
              "funzionalità di fegato e reni",
            ],
            after: "In molte condizioni ematologiche, l'emocromo e alcuni esami \u201Cdi chimica\u201D sono fondamentali per la valutazione iniziale e il follow-up.",
          },
          {
            text: "Referti di esami precedenti pertinenti:",
            sublist: [
              "strisci di sangue periferico, biopsie/aspirati midollari, immunofenotipo/flow, FISH/genetica, istologici",
              "TAC/PET/eco/risonanze, se eseguite",
              "lettere di dimissione o relazioni di altri specialisti",
            ],
          },
          "Se hai fatto trasfusioni o hai una storia trasfusionale complessa: porta la documentazione disponibile (utile per pianificare eventuali necessità future).",
        ],
      },
      {
        heading: "Prima della visita: esami e digiuno",
        list: [
          "Non serve il digiuno \u201Cdi routine\u201D per una visita ematologica, ma alcuni esami richiedono digiuno (ad esempio profilo lipidico o glicemia in certi percorsi terapeutici). Se ti è stato richiesto un prelievo \u201Ca digiuno\u201D, di solito significa 8–12 ore senza cibo, acqua consentita.",
          "Se devi fare esami del ferro (ferro, ferritina, transferrina/TSAT) e stai assumendo ferro per bocca, chiedi se sospenderlo prima del prelievo: in alcuni protocolli è consigliato interromperlo 48 ore prima del dosaggio del ferro.",
          {
            text: "Se hai un prelievo lo stesso giorno della visita:",
            sublist: [
              "idratazione normale (bere acqua) può rendere il prelievo più facile",
              "evita sforzi intensi subito prima, se possibile",
            ],
          },
        ],
      },
      {
        heading: "Sintomi: preparati a raccontarli in modo chiaro",
        paragraphs: ["Annota (anche su un foglio o sul telefono):"],
        list: [
          "da quando sono iniziati i sintomi",
          "se sono peggiorati o migliorati",
          {
            text: "episodi di:",
            sublist: [
              "febbre o infezioni ricorrenti",
              "stanchezza marcata, fiato corto, capogiri",
              "sanguinamenti \u201Cinsoliti\u201D (gengive, naso, urine, feci), lividi frequenti",
              "perdita di peso, sudorazioni notturne, linfonodi ingrossati",
              "dolore osseo, prurito, ittero (pelle/occhi gialli)",
            ],
          },
        ],
      },
      {
        heading: "Informazioni personali importanti da comunicare",
        list: [
          "Allergie (farmaci, lattice, ecc.)",
          "Gravidanza o possibilità di gravidanza (se rilevante)",
          "Interventi recenti, viaggi, infezioni recenti",
          "Familiarità per malattie del sangue o tumori del sangue",
        ],
      },
      {
        heading: "Se è prevista (o possibile) una procedura invasiva",
        paragraphs: [
          "A volte, dopo la visita o in un secondo momento, può essere proposta una procedura (es. biopsia/aspirato midollare o altre biopsie). In quel caso:",
        ],
        list: [
          "non sospendere da solo anticoagulanti o antiaggreganti: la gestione dipende dal tipo di procedura e dal tuo rischio trombotico/emorragico; va deciso insieme allo specialista.",
          "potrebbero essere richiesti emocromo e test della coagulazione vicini alla procedura, secondo indicazione clinica.",
        ],
      },
      {
        heading: "Il giorno della visita: consigli pratici",
        list: [
          "Arriva con un po' di anticipo per accettazione e documenti.",
          "Indossa abiti comodi (maniche facili da alzare se è previsto un prelievo).",
          "Se ti senti debole o hai avuto svenimenti ai prelievi, dillo subito: il personale può farti fare il prelievo da sdraiato.",
        ],
      },
      {
        heading: "Dopo la visita",
        paragraphs: ["Chiedi sempre:"],
        list: [
          "quali esami fare e quando",
          "se servono preparazioni (digiuno/sospensione integratori)",
          "quando e come riceverai i risultati",
          "quali sintomi devono farti contattare subito (o andare in PS)",
        ],
      },
    ],
    disclaimer:
      "Questa guida non sostituisce il parere medico. Se i sintomi peggiorano o compaiono nuovi problemi (es. febbre alta, sanguinamenti importanti, mancanza di respiro), contatta il medico o rivolgiti al Pronto Soccorso.",
  },
  "vivere-con-linfoma": {
    tipo: "Guida",
    icon: BookOpen,
    titolo: "Vivere con un linfoma: percorso e follow-up",
    descrizione:
      "Come si svolge il follow-up, quando servono TAC/PET, effetti tardivi e qualità di vita.",
    intro:
      "Dopo la diagnosi e le cure, vivere con un linfoma significa seguire un percorso di controlli (follow-up) e prendersi cura della propria salute nel lungo periodo, per intercettare tempestivamente eventuali ricadute, gestire effetti collaterali tardivi e migliorare la qualità di vita.",
    sections: [
      {
        heading: "Impostazione generale del follow-up",
        paragraphs: [
          "Nei pazienti in remissione completa, il follow-up deve basarsi soprattutto su visita (anamnesi + esame obiettivo) a intervalli regolari; in generale non si raccomandano PET/TC o TC di \u201Croutine\u201D se non ci sono sintomi o segni sospetti.",
        ],
      },
      {
        heading: "Obiettivi del follow-up",
        list: [
          "Controllare che la malattia resti sotto controllo e riconoscere presto segnali di possibile ripresa.",
          "Gestire effetti collaterali \u201Ctardivi\u201D (che possono comparire mesi o anni dopo le cure).",
          "Supportare la qualità di vita: stanchezza, sonno, umore, lavoro, sessualità, fertilità, attività fisica.",
        ],
      },
      {
        heading: "Come si svolgono i controlli (in pratica)",
        paragraphs: ["Durante le visite di follow-up, di solito si fa:"],
        list: [
          "Colloquio su sintomi, energie, febbre/infezioni, perdita di peso, sudorazioni notturne, dolore, prurito, tosse o fiato corto.",
          "Visita clinica: controllo dei linfonodi, milza e fegato.",
          "Esami del sangue quando indicato (spesso emocromo e \u201Cchimica\u201D di base), per controllare sia la malattia sia eventuali effetti delle terapie.",
        ],
      },
      {
        heading: "TAC/PET: quando servono davvero",
        list: [
          "In molte situazioni, fare TAC o PET di routine in assenza di sintomi non è utile e può aumentare ansia e accertamenti inutili.",
          "Le immagini si usano soprattutto se compaiono sintomi o segni sospetti, o se lo specialista lo ritiene necessario per il tuo tipo di linfoma e la tua storia.",
        ],
      },
      {
        heading: "Calendario indicativo delle visite",
        paragraphs: [
          "Il follow-up dipende dal tipo di linfoma, dalle terapie fatte e da come stai. In generale:",
        ],
        subsections: [
          {
            title: "Linfomi aggressivi trattati con intento \u201Ccurativo\u201D (es. DLBCL)",
            list: [
              "Visita ogni 3–6 mesi per i primi 2 anni.",
              "Poi controlli più diradati; spesso, se tutto va bene, il follow-up può anche essere ridotto/interrotto dopo alcuni anni, secondo valutazione specialistica.",
            ],
          },
          {
            title: "Linfomi indolenti (es. linfoma follicolare)",
            list: [
              "Controlli con frequenza simile all'inizio (spesso ogni 3–6 mesi), poi ogni 6–12 mesi nel tempo.",
              "In alcuni casi il linfoma può comportarsi come una malattia cronica: si alternano periodi lunghi di benessere a eventuali fasi in cui serve terapia.",
            ],
          },
          {
            title: "Linfoma di Hodgkin",
            list: [
              "In genere controlli più ravvicinati all'inizio (es. ogni 3 mesi nel primo anno, poi ogni 6 mesi per alcuni anni, poi annuali), con particolare attenzione agli effetti tardivi.",
            ],
          },
        ],
        callout:
          "Il calendario può variare da persona a persona: lo specialista lo adatterà al tuo tipo di linfoma, alla terapia effettuata e all'andamento clinico.",
      },
      {
        heading: "Effetti tardivi: cosa monitorare nel tempo",
        paragraphs: [
          "Alcune cure possono avere effetti che emergono più tardi. Non significa che succederà a te, ma è utile saperlo.",
        ],
        subsections: [
          {
            title: "Cuore e vasi",
            text:
              "Alcune chemioterapie (es. antracicline) e alcune radioterapie possono aumentare il rischio di problemi cardiaci nel tempo: segnala fiato corto, dolore toracico, palpitazioni, gonfiore alle gambe.",
          },
          {
            title: "Tiroide (se radioterapia al collo)",
            text: "Può essere richiesto un controllo periodico della funzione tiroidea.",
          },
          {
            title: "Secondi tumori (controlli di prevenzione)",
            list: [
              "È importante seguire gli screening raccomandati per età (es. colon, pelle, ecc.).",
              "Se hai fatto radioterapia al torace/ascella da giovane donna, può essere raccomandato uno screening del seno più precoce e strutturato (lo specialista ti dirà quando iniziare e con quali esami).",
            ],
          },
          {
            title: "Infezioni e vaccini",
            text:
              "Dopo alcune terapie, le difese immunitarie possono restare più deboli per un periodo: parla con l'ematologo di vaccini stagionali (es. influenza, COVID-19) e di cosa fare in caso di febbre.",
          },
        ],
      },
      {
        heading: "Qualità di vita: è parte della cura",
        paragraphs: ["Dopo le terapie, è comune avere:"],
        list: [
          "stanchezza persistente",
          "formicolii o dolore ai nervi (neuropatia)",
          "ansia o paura della ricaduta",
          "difficoltà nel sonno o nel rientro al lavoro",
        ],
        afterList:
          "Questi aspetti meritano attenzione quanto gli esami: se li riferisci, possiamo proporre strategie e supporti mirati (es. riabilitazione, supporto psicologico, gestione del dolore, attività fisica adattata).",
      },
      {
        heading: "Quando contattare subito l'ematologo (o valutare urgenza)",
        paragraphs: ["Contatta il centro/ambulatorio se compaiono:"],
        list: [
          "febbre (specialmente se >38°C) o infezioni ricorrenti",
          "sudorazioni notturne, perdita di peso non spiegata, stanchezza marcata in peggioramento",
          "nuovi linfonodi che crescono rapidamente o dolore/pienezza addominale",
          "fiato corto, tosse persistente, dolore toracico",
          "sanguinamenti insoliti o lividi facili",
        ],
      },
      {
        heading: "Cosa puoi fare tu: piccoli passi utili",
        list: [
          "Porta sempre un elenco aggiornato dei farmaci e dei referti.",
          "Tieni un diario dei sintomi (anche breve).",
          "Segui uno stile di vita protettivo: attività fisica regolare, alimentazione equilibrata, stop fumo, alcol con moderazione (se consentito dal medico).",
        ],
      },
    ],
    disclaimer:
      "Questa guida non sostituisce il parere medico. Se i sintomi peggiorano o hai nuove preoccupazioni, contatta il tuo ematologo o rivolgiti al Pronto Soccorso.",
  },
};

// -------------------- Card grid --------------------
const placeholders = [
  {
    key: "anemia-sideropenica",
    tipo: "Guida",
    titolo: "Cos'è l'anemia sideropenica",
    descrizione:
      "Guida completa: cause, sintomi, diagnosi e cura dell'anemia da carenza di ferro.",
    icon: BookOpen,
    stato: "Disponibile",
    hasArticle: true,
  },
  {
    key: "prima-visita-ematologica",
    tipo: "Guida",
    titolo: "Prepararsi alla prima visita ematologica",
    descrizione:
      "Documentazione, farmaci, esami e sintomi da segnalare: come arrivare preparati alla visita.",
    icon: BookOpen,
    stato: "Disponibile",
    hasArticle: true,
  },
  {
    tipo: "Risorsa esterna",
    titolo: "I diritti del malato oncologico",
    descrizione:
      "Guida ufficiale sul Portale INPS: tutele, agevolazioni e diritti previdenziali per il paziente oncologico.",
    icon: Landmark,
    stato: "Portale INPS",
    externalLink: "https://customer-assets.emergentagent.com/job_studio-ematologico/artifacts/7funk8ln_diritti_tutele_malattie_oncologiche.pdf",
  },
  {
    key: "vivere-con-linfoma",
    tipo: "Guida",
    titolo: "Vivere con un linfoma: percorso e follow-up",
    descrizione:
      "Come si svolge il follow-up, quando servono TAC/PET, effetti tardivi e qualità di vita.",
    icon: BookOpen,
    stato: "Disponibile",
    hasArticle: true,
  },
];

// -------------------- Article renderer --------------------
function ArticleBody({ article }) {
  return (
    <div className="mt-2 space-y-8 text-[15px] leading-relaxed text-[#1A1D1A]/90">
      <p className="text-[16px] text-[#1A1D1A]">{article.intro}</p>

      {article.sections.map((sec, i) => (
        <section key={i}>
          <h3 className="font-serif-display text-2xl leading-snug text-[#1A1D1A] mb-4">
            {sec.heading}
          </h3>

          {sec.paragraphs?.map((p, j) => (
            <p key={j} className="mb-3">
              {p}
            </p>
          ))}

          {sec.list && (
            <ul className="mt-2 space-y-2">
              {sec.list.map((it, k) => {
                if (typeof it === "string") {
                  return (
                    <li key={k} className="flex gap-3">
                      <span
                        className="mt-[9px] w-1.5 h-1.5 rounded-full bg-[#8A6D56] shrink-0"
                        aria-hidden
                      />
                      <span>{it}</span>
                    </li>
                  );
                }
                return (
                  <li key={k} className="flex gap-3">
                    <span
                      className="mt-[9px] w-1.5 h-1.5 rounded-full bg-[#8A6D56] shrink-0"
                      aria-hidden
                    />
                    <div className="flex-1">
                      <span>{it.text}</span>
                      {it.sublist && (
                        <ul className="mt-2 ml-4 space-y-1.5">
                          {it.sublist.map((s, m) => (
                            <li key={m} className="flex gap-3 text-[#5C635E]">
                              <span
                                className="mt-[8px] w-1 h-1 rounded-full bg-[#5C635E] shrink-0"
                                aria-hidden
                              />
                              <span>{s}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                      {it.after && (
                        <p className="mt-2 text-[#5C635E] text-[14px] italic">{it.after}</p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {sec.afterList && (
            <p className="mt-4 text-[#5C635E] italic">{sec.afterList}</p>
          )}

          {sec.callout && (
            <div className="mt-6 border-l-2 border-[#8A6D56] pl-5 py-2 bg-[#EFECE7]/60 rounded-r">
              <p className="text-[14px] text-[#1A1D1A]">{sec.callout}</p>
            </div>
          )}

          {sec.subsections?.map((sub, k) => (
            <div key={k} className="mt-6">
              <p className="font-semibold text-[#1A1D1A] mb-2">{sub.title}</p>
              {sub.text && <p>{sub.text}</p>}
              {sub.list && (
                <ul className="mt-2 space-y-2">
                  {sub.list.map((it, m) => (
                    <li key={m} className="flex gap-3">
                      <span
                        className="mt-[9px] w-1.5 h-1.5 rounded-full bg-[#8A6D56] shrink-0"
                        aria-hidden
                      />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </section>
      ))}

      <div className="mt-8 pt-6 border-t border-[#E5E1D8]">
        <p className="text-[13px] text-[#5C635E] italic">{article.disclaimer}</p>
      </div>
    </div>
  );
}

// -------------------- Section --------------------
export default function EducazionePaziente() {
  return (
    <section
      id="educazione"
      data-testid="educazione-section"
      className="py-24 md:py-32 bg-[#F9F8F6] border-t border-[#E5E1D8]"
    >
      <div className="max-w-[1320px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-16">
          <div className="lg:col-span-7">
            <p className="eyebrow">Educazione del paziente</p>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight mt-6 text-[#1A1D1A]">
              Guide per <span className="italic text-[#8A6D56]">capire</span>.
            </h2>
          </div>
          <p className="lg:col-span-5 text-base text-[#5C635E] leading-relaxed">
            Uno spazio dedicato all&apos;informazione clinica per il paziente:
            contenuti divulgativi rigorosi, guide informative e materiali in preparazione.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {placeholders.map((p, i) => {
            const Icon = p.icon;
            const article = p.hasArticle ? articles[p.key] : null;
            const isExternal = !!p.externalLink;
            const isInteractive = article || isExternal;

            const cardBody = (
              <motion.article
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                data-testid={`educazione-card-${i}`}
                className={`h-full bg-white border border-[#E5E1D8] rounded-2xl p-8 flex flex-col text-left ${
                  isInteractive ? "cursor-pointer hover:border-[#8A6D56]/60 hover:-translate-y-1 transition-[transform,border-color] duration-300" : ""
                }`}
              >
                <div className="flex items-center justify-between">
                  <Icon className="w-6 h-6 text-[#8A6D56]" strokeWidth={1.5} aria-hidden />
                  <span className="eyebrow text-[10px] text-[#5C635E]">{p.tipo}</span>
                </div>
                <h3 className="font-serif-display text-xl leading-tight mt-8 text-[#1A1D1A]">
                  {p.titolo}
                </h3>
                <p className="text-[13px] text-[#5C635E] leading-relaxed mt-3 flex-1">
                  {p.descrizione}
                </p>
                <div className="mt-6 pt-6 border-t border-[#E5E1D8] flex items-center justify-between">
                  <span className="text-[11px] uppercase tracking-[0.18em] text-[#8A6D56] font-semibold">
                    {p.stato}
                  </span>
                  <span className={`text-[13px] ${isInteractive ? "text-[#8A6D56] font-semibold" : "text-[#1A1D1A]/40 cursor-not-allowed"}`}>
                    {article ? "Leggi →" : isExternal ? "Apri ↗" : "In arrivo →"}
                  </span>
                </div>
              </motion.article>
            );

            if (isExternal) {
              return (
                <a
                  key={p.titolo}
                  href={p.externalLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`educazione-external-${i}`}
                  className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8A6D56] focus-visible:ring-offset-2 rounded-2xl"
                >
                  {cardBody}
                </a>
              );
            }

            if (!article) return <div key={p.titolo}>{cardBody}</div>;

            return (
              <Dialog key={p.titolo}>
                <DialogTrigger asChild>
                  <button
                    data-testid={`educazione-open-${p.key}`}
                    className="text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8A6D56] focus-visible:ring-offset-2 rounded-2xl"
                  >
                    {cardBody}
                  </button>
                </DialogTrigger>
                <DialogContent
                  className="max-w-3xl w-[95vw] max-h-[88vh] overflow-y-auto bg-[#F9F8F6] border border-[#E5E1D8] rounded-2xl p-8 md:p-12"
                  data-testid={`educazione-article-${p.key}`}
                >
                  <DialogHeader className="text-left">
                    <p className="eyebrow text-[#8A6D56]">{article.tipo}</p>
                    <DialogTitle className="font-serif-display text-3xl md:text-4xl leading-tight text-[#1A1D1A] mt-3">
                      {article.titolo}
                    </DialogTitle>
                  </DialogHeader>
                  <ArticleBody article={article} />
                </DialogContent>
              </Dialog>
            );
          })}
        </div>

        <p className="mt-12 text-sm text-[#5C635E] max-w-3xl">
          Sezione predisposta per ospitare guide informative e video divulgativi. I
          contenuti verranno pubblicati progressivamente.
        </p>
      </div>
    </section>
  );
}
